import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { Eyebrow, PageHeader, SpecStrip } from "../components/bits";
import { PRODUCTS, IMG, RECEIVER_COMPARISON } from "../data/content";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";

export default function Products() {
  return (
    <Layout>
      <PageHeader
        eyebrow="The hardware family"
        title="One account. A whole family of power."
        subtitle="Surfaces for desks and nightstands. Ceiling units for whole-room top-up. Receivers that upgrade the devices you already own. Revo Go for the road. And the Revo Kitchen, where appliances run with no cords and no batteries at all."
      />

      <div className="bg-white">
        {PRODUCTS.map((p, i) => (
          <section key={p.id} id={p.id} className={`px-5 py-20 md:px-8 ${i % 2 ? "bg-revo-surface" : "bg-white"}`}>
            <div className={`mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <Reveal>
                {p.before ? (
                  <>
                    <BeforeAfterSlider before={p.before} after={p.after} beforeLabel="Bare laptop" afterLabel="Tag installed" testid="tag-before-after" />
                    <p className="mt-3 text-center text-sm italic text-navy/50">Drag to see how invisible it is once installed.</p>
                  </>
                ) : (
                  <div className="group relative overflow-hidden rounded-3xl border border-navy/10 bg-white">
                    <div className="aspect-square overflow-hidden">
                      <img src={p.img} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    {p.badge && (
                      <span className="absolute left-4 top-4 rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-revo-green">{p.badge}</span>
                    )}
                  </div>
                )}
                {p.macro && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className="overflow-hidden rounded-2xl border border-navy/10 bg-white" style={{ flex: "0 0 42%" }}>
                      <div className="aspect-square overflow-hidden">
                        <img src={p.macro} alt={`${p.name} detail`} loading="lazy" className="h-full w-full object-cover" />
                      </div>
                    </div>
                    <p className="text-xs uppercase tracking-[0.2em] text-navy/40">Precision-machined<br />chassis · detail</p>
                  </div>
                )}
                {p.honesty && <p className="mt-3 text-center text-sm italic text-navy/50">{p.honesty}</p>}
              </Reveal>
              <Reveal delay={0.1}>
                <Eyebrow dark>{p.name}</Eyebrow>
                <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">{p.tagline}</h2>
                <p className="mt-5 text-lg leading-relaxed text-navy/70">{p.desc}</p>
                <div className="mt-6"><SpecStrip specs={p.specs} dark /></div>
              </Reveal>
            </div>
          </section>
        ))}

        {/* RECEIVER COMPARISON */}
        <section id="receiver-comparison" className="bg-revo-surface px-5 py-20 md:px-8" data-testid="receiver-comparison">
          <div className="mx-auto max-w-5xl">
            <Reveal>
              <Eyebrow dark>Three ways to enroll a laptop</Eyebrow>
              <h2 className="mt-3 max-w-2xl font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">Base, Key, or Tag — pick how your laptop joins.</h2>
              <p className="mt-4 max-w-2xl text-navy/60">Every option carries its own Revo serial and charges on any Surface. The difference is how it mounts and whether it moves with you.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10 overflow-hidden rounded-3xl border border-navy/10 bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] border-collapse text-left">
                    <thead>
                      <tr className="border-b border-navy/10 bg-navy">
                        <th className="p-5 text-sm font-semibold text-revo-ice/70"></th>
                        {RECEIVER_COMPARISON.cols.map((c, i) => (
                          <th key={c} className={`p-5 font-display text-base font-extrabold ${i === 2 ? "text-revo-green" : "text-white"}`}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {RECEIVER_COMPARISON.rows.map((row) => (
                        <tr key={row.label} className="border-b border-navy/5 last:border-0">
                          <th className="p-5 align-top text-sm font-bold text-navy">{row.label}</th>
                          {row.values.map((v, i) => (
                            <td key={i} className={`p-5 align-top text-sm ${i === 2 ? "bg-revo-green/[0.06] font-semibold text-navy" : "text-navy/70"}`}>{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Reveal>
          </div>
        </section>


        <section className="bg-white px-5 pb-4 pt-20 md:px-8">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <Eyebrow dark>In every room</Eyebrow>
              <h2 className="mt-3 max-w-2xl font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">One account. The whole house, always charged.</h2>
            </Reveal>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <Reveal>
                <div className="overflow-hidden rounded-3xl border border-navy/10" data-testid="room-living">
                  <div className="aspect-[16/10] overflow-hidden"><img src={IMG.homeFamily} alt="Living room with laptop, phone and cordless vacuum all charging" className="h-full w-full object-cover" /></div>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="overflow-hidden rounded-3xl border border-navy/10" data-testid="room-garage">
                  <div className="aspect-[16/10] overflow-hidden"><img src={IMG.garage} alt="Garage shelf strip charging cordless tools and a robot vacuum" className="h-full w-full object-cover" /></div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* CONCEPT LAPTOP */}
        <section className="relative overflow-hidden bg-navy px-5 py-24 md:px-8 grain">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <span className="inline-block rounded-full border border-revo-green/40 bg-revo-green/10 px-3 py-1.5 text-xs font-bold text-revo-green">Concept — in development with OEM partners</span>
              <h2 className="mt-4 font-display text-3xl font-black tracking-tight text-white sm:text-5xl">Coming: the first laptop with no charging port.</h2>
              <p className="mt-5 text-lg leading-relaxed text-revo-ice">
                Built with OEM partners under the Revo Inside program: a base coil for 65–100W surface charging, a lid harvesting array for ambient top-up, and an extreme-fast-charge battery that refills in minutes. No barrel jack. No charger in the box. Nothing to plug in — ever.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/technology" data-testid="concept-read-btn" className="flex items-center gap-2 rounded-full bg-revo-green px-6 py-3 font-bold text-navy transition-transform duration-200 hover:scale-105">
                  Read the concept <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/business" data-testid="concept-oem-btn" className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold text-white transition-colors hover:border-revo-green">
                  OEM partners: talk to us
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-3xl border border-white/10">
                <img src={IMG.laptopConcept} alt="Revo-native laptop concept with no charging port" className="w-full" />
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </Layout>
  );
}
